import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { ResourceHero } from "@/components/site/ResourceHero";
import { irsNotices } from "@/data/irsNotices";
import { stockImages } from "@/data/site/stockImages";
import { RevealStagger, RevealItem } from "@/components/motion/Reveal";

const urgencyStyles: Record<string, { label: string; chip: string; accent: string }> = {
  low: {
    label: "Low urgency",
    chip: "bg-blue-50 text-blue-800 border-blue-200",
    accent: "bg-blue-500",
  },
  medium: {
    label: "Act soon",
    chip: "bg-amber-50 text-amber-900 border-amber-200",
    accent: "bg-amber-500",
  },
  high: {
    label: "Urgent",
    chip: "bg-orange-50 text-orange-900 border-orange-200",
    accent: "bg-orange-500",
  },
  critical: {
    label: "Critical — act now",
    chip: "bg-red-50 text-red-800 border-red-200",
    accent: "bg-red-600",
  },
};

const IRSNoticesIndex = () => {
  const criticalCount = irsNotices.filter((n) => n.urgency === "critical" || n.urgency === "high").length;
  return (
  <SiteLayout>
    <PageSEO
      title="IRS Notice Library — Decode Every IRS Letter"
      description="Got a CP14, CP2000, LT11, or Notice of Deficiency? Look up every common IRS notice — what it means, the deadline, and what to do next."
      path="/resources/irs-notices"
      image={stockImages.noticeEnvelope.src}
    />
    <ResourceHero
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "IRS Notices" },
      ]}
      eyebrow="Notice library"
      title="Decode any IRS letter"
      summary="What each IRS notice means, the response deadline, what happens if you ignore it, and what to do next. If you just opened a letter from the IRS, find it here first."
      facts={[
        { label: "Notices covered", value: irsNotices.length },
        { label: "Urgent / critical", value: criticalCount },
        {
          label: "Notice lookup",
          value: (
            <Link to="/resources/notice-lookup" className="text-gold hover:underline">
              Open tool →
            </Link>
          ),
        },
      ]}
      background="noticeEnvelope"
    />


    <section className="py-12 lg:py-16 bg-secondary/40">
      <div className="container mx-auto px-4 max-w-6xl">
        <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" stagger={50}>
          {irsNotices.map((n) => {
            const u = urgencyStyles[n.urgency];
            return (
              <RevealItem key={n.id} className="h-full">
                <Link
                  to={`/resources/irs-notices/${n.id}`}
                  aria-label={`Read details for IRS Notice ${n.number} — ${n.name}`}
                  className="group relative flex flex-col bg-white rounded-lg border border-border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden h-full"
                >
                  <span className={`absolute inset-x-0 top-0 h-1 ${u.accent}`} aria-hidden />
                  {n.image && (
                    <div className="aspect-[8.5/5] overflow-hidden bg-muted/40 border-b border-border">
                      <img
                        src={n.image}
                        alt={`Sample first page of IRS Notice ${n.number}`}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                       width={816} height={1056} />
                    </div>
                  )}
                  <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="font-heading font-bold text-navy text-xl group-hover:text-gold transition-colors">
                        {n.number}
                      </span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full border ${u.chip}`}
                      >
                        {u.label}
                      </span>
                    </div>
                    <h2 className="font-heading font-semibold text-navy text-lg mb-3 leading-snug">
                      {n.name}
                    </h2>
                    <p className="text-sm text-foreground/75 line-clamp-3 leading-relaxed mb-4">
                      {n.description}
                    </p>
                    <div className="mt-auto flex items-center justify-between pt-3 border-t border-border/70">
                      {n.deadline ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" /> Respond within {n.deadline}
                        </span>
                      ) : (
                        <span />
                      )}
                      <span className="text-xs font-bold text-navy group-hover:text-gold inline-flex items-center gap-1">
                        Read notice {n.number} details <ArrowRight className="w-3.5 h-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              </RevealItem>
            );
          })}
        </RevealStagger>
      </div>
    </section>

    <CTASection />
  </SiteLayout>
  );
};


export default IRSNoticesIndex;
